/**
 * Preset-aware colour settings.
 *
 * Core's PanelColorSettings reports and matches on resolved values: pick
 * "Brandy Primary" and the callback receives `#377E62`, with nothing left to say
 * which swatch produced it. A stored value that is anything other than a
 * swatch's exact colour string leaves every swatch unselected and the control
 * showing "Custom" instead. Both halves of that are wrong for a block whose
 * colours are meant to track the palette.
 *
 * This maps in both directions around core's panel, so the familiar UI is kept
 * and only the value contract changes: the control sees the concrete colour it
 * needs to paint a swatch selected, while the attribute stores
 * `var:preset|color|<slug>` — a live reference that follows a retheme or a niche
 * switch instead of freezing today's hex.
 */

import { PanelColorSettings } from '@wordpress/block-editor';
import { useSelect } from '@wordpress/data';
import { useMemo } from '@wordpress/element';

import {
	fromColorPreset,
	getEditorColorPalette,
	toColorPreset,
} from './helpers';

/**
 * The editor's palette, flattened to the entries a preset lookup needs.
 *
 * The same accessor core's own colour controls resolve against, so a swatch
 * this maps to is a swatch the control will actually mark selected.
 *
 * Exported for controls built on a different core primitive — the tools-panel
 * dropdowns take `colorValue`/`onColorChange` rather than `value`/`onChange`, so
 * they map their own entries but must resolve against the same palette.
 *
 * @return {Array} Flat palette entries ({ slug, color, name }).
 */
export function usePresetPalette() {
	return useSelect( getEditorColorPalette, [] );
}

/**
 * Drop-in replacement for PanelColorSettings that stores palette references.
 *
 * Takes and forwards every prop core's panel takes; only the `colorSettings`
 * entries are rewritten.
 *
 * @param {Object} props               Panel props.
 * @param {Array}  props.colorSettings Entries of { value, onChange, label }.
 * @return {Element} The panel.
 */
export default function PresetColorSettings( {
	colorSettings = [],
	...panelProps
} ) {
	const palette = usePresetPalette();

	const settings = useMemo(
		() =>
			colorSettings.map( ( setting ) => ( {
				...setting,
				value: fromColorPreset( setting.value, palette ),
				onChange: ( next ) =>
					setting.onChange( toColorPreset( next, palette ) ),
			} ) ),
		[ colorSettings, palette ]
	);

	return <PanelColorSettings { ...panelProps } colorSettings={ settings } />;
}
